import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FormSection,
  Slider,
  Checkbox,
  RadioGroup,
  Switch,
} from "@eweadn-qiankun/shared-components";
import { getSupportedDevicesSync } from "@eweadn-qiankun/shared-utils";
import type { CommunityPreviewDeviceMeta } from "@eweadn-qiankun/shared-types";
import SensorRotation from "../../../../../mouse-management/src/pages/parameter/SensorRotation";
import {
  getLodOptionsBySensor,
  JIANGMENG_3955_LOD_MM_MAX,
  JIANGMENG_3955_LOD_MM_MIN,
  JIANGMENG_3955_LOD_MM_STEP,
  ZHANLI_3955_LOD_MM_MAX,
  ZHANLI_3955_LOD_MM_MIN,
  ZHANLI_3955_LOD_MM_STEP,
  isJiangmeng3955LodSlider,
  jiangmeng3955LodDeviceValueToMm,
  zhanli3955LodDeviceValueToMm,
} from "../../../../../mouse-management/src/domain/sensorRules";
import { mouseIsAlpha6 } from "../../../../../mouse-management/src/domain/mouseIdentity";
import {
  ALPHA6_SLEEP_MAX_SEC,
  ALPHA6_SLEEP_MIN_SEC,
  ALPHA6_SLEEP_STEP_SEC,
  JIANGMENG_SLEEP_LEGACY_MAX_SEC,
  JIANGMENG_SLEEP_LEGACY_MIN_SEC,
  jiangmengSleepLevelToSeconds,
} from "../../../../../mouse-management/src/domain/jiangmengSleepTime";
import {
  getBeiyingMouseParameterPreviewValues,
  getFreqParameterPreviewValues,
  getMouseParameterPreviewValues,
  getZhanliMouseParameterPreviewValues,
  resolveFreqLinkSchemeForPreview,
} from "../utils/mouseConfigPreviewUtils";
import "../../../../../mouse-management/src/pages/parameter/index.scss";
import "../../../../../mouse-management/src/pages/parameter/SensorRotation.scss";

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}

function FreqParameterPreview({
  meta,
  configData,
}: {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const linkScheme = useMemo(
    () => resolveFreqLinkSchemeForPreview(meta, configData),
    [meta, configData]
  );
  const values = useMemo(
    () => getFreqParameterPreviewValues(configData),
    [configData]
  );
  const isSchemeA = linkScheme === "a";
  const noop = () => undefined;

  return (
    <div className="mouse-setting-content">
      <div className="setting-column">
        {isSchemeA ? (
          <FormSection
            title={t("mouse.parameter.straightCorrection")}
            description={t("mouse.parameter.straightCorrectionDesc")}
            rightContent={
              <Switch checked={values.straightCorrection} onChange={noop} />
            }
          />
        ) : null}
        <FormSection
          title={t("mouse.parameter.freqSleepSetting")}
          description={t("mouse.parameter.freqSleepSettingDesc")}
        >
          <div className="community-preview-inline-row">
            <div className="parameter-slider-wrap" style={{ flex: 1 }}>
              <Slider
                min={0}
                max={100}
                step={1}
                value={values.sleepMinutes}
                onChange={noop}
                showValue
                unit={t("mouse.parameter.freqSleepMinuteUnit")}
              />
            </div>
            <Checkbox
              className="checkbox-container"
              label={t("mouse.parameter.neverSleep")}
              checked={values.sleepMinutes === 0}
              onChange={noop}
            />
          </div>
        </FormSection>
      </div>
      <div className="setting-column">
        <FormSection
          title={t("mouse.parameter.freqScrollDirection")}
          description={t("mouse.parameter.freqScrollDirectionDesc")}
        >
          <RadioGroup
            options={[
              { label: t("mouse.parameter.freqScrollStandard"), value: 0 },
              { label: t("mouse.parameter.freqScrollInverted"), value: 1 },
            ]}
            value={values.scrollFlag}
            onChange={noop}
            direction="horizontal"
          />
        </FormSection>
      </div>
    </div>
  );
}

function BeiyingParameterPreview({
  meta,
  configData,
}: {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const values = useMemo(
    () => getBeiyingMouseParameterPreviewValues(configData),
    [configData]
  );

  const lodOptions = useMemo(() => {
    const mice = getSupportedDevicesSync().mouse as Array<{
      deviceId?: string;
      sensor?: string;
    }>;
    const match = mice.find((item) => item.deviceId === meta.mouseDeviceId);
    const sensor = match?.sensor ?? "3950";
    return getLodOptionsBySensor(sensor, "beiying");
  }, [meta.mouseDeviceId]);

  const lodValue = useMemo(() => {
    const inOptions = lodOptions.some((item) => item.value === values.lod);
    return inOptions ? values.lod : (lodOptions[0]?.value ?? values.lod);
  }, [lodOptions, values.lod]);

  const noop = () => undefined;
  const sleepSeconds = values.neverSleep ? 0 : values.sleepLevel * 30;

  return (
    <div className="mouse-setting-content">
      <div className="setting-column">
        <FormSection
          title={t("mouse.parameter.motionSync")}
          description={t("mouse.parameter.motionSyncDesc")}
          rightContent={<Switch checked={values.motionSync} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.rippleCorrection")}
          description={t("mouse.parameter.rippleCorrectionDesc")}
          rightContent={<Switch checked={values.ripple} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.sleepTime")}
          description={t("mouse.parameter.sleepTimeDescBeiying")}
        >
          <div className="community-preview-inline-row">
            <div className="parameter-slider-wrap" style={{ flex: 1 }}>
              <Slider
                min={0}
                max={3000}
                step={30}
                value={sleepSeconds}
                onChange={noop}
                showValue
                unit="s"
                disabled={values.neverSleep}
              />
            </div>
            <Checkbox
              className="checkbox-container"
              label={t("mouse.parameter.neverSleep")}
              checked={values.neverSleep}
              onChange={noop}
            />
          </div>
        </FormSection>
      </div>
      <div className="setting-column">
        <FormSection
          title={t("mouse.parameter.straightCorrection")}
          description={t("mouse.parameter.straightCorrectionDesc")}
          rightContent={<Switch checked={values.angle} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.lodHeight")}
          description={t("mouse.parameter.lodHeightDesc")}
        >
          <RadioGroup
            options={lodOptions}
            value={lodValue}
            onChange={noop}
            direction="horizontal"
          />
        </FormSection>
        <FormSection
          title={t("mouse.parameter.debounceDelay")}
          description={t("mouse.parameter.debounceDelayDescBeiying")}
        >
          <div className="parameter-slider-wrap">
            <Slider
              min={0}
              max={20}
              value={values.debounceTime}
              onChange={noop}
              showValue
              unit="ms"
            />
          </div>
        </FormSection>
      </div>
    </div>
  );
}

/** 对齐子应用 `parameter/zhanli` 左右列与控件 */
function ZhanliParameterPreview({
  configData,
}: {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const values = useMemo(
    () => getZhanliMouseParameterPreviewValues(configData),
    [configData]
  );

  const lodMmPreview = zhanli3955LodDeviceValueToMm(values.lod);

  const noop = () => undefined;
  const virtualOffsetDisplayUi = values.virtualOffsetEnable
    ? values.virtualOffsetUi
    : 0;

  return (
    <div className="mouse-setting-content">
      <div className="setting-column">
        <FormSection
          title={t("mouse.parameter.motionSync")}
          description={t("mouse.parameter.motionSyncDesc")}
          rightContent={<Switch checked={values.motionSync} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.straightCorrection")}
          description={t("mouse.parameter.straightCorrectionDesc")}
          rightContent={<Switch checked={values.angle} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.lodHeight")}
          description={t("mouse.parameter.lodHeightDesc")}
        >
          <div className="parameter-slider-wrap">
            <Slider
              min={ZHANLI_3955_LOD_MM_MIN}
              max={ZHANLI_3955_LOD_MM_MAX}
              step={ZHANLI_3955_LOD_MM_STEP}
              value={lodMmPreview}
              onChange={noop}
              showValue
              unit="mm"
              disabled
              scaleByStep
            />
          </div>
        </FormSection>
        <FormSection
          title={t("mouse.parameter.sleepTime")}
          description={t("mouse.parameter.zhanliSleepTimeDesc")}
        >
          <div className="community-preview-inline-row">
            <div className="parameter-slider-wrap" style={{ flex: 1 }}>
              <Slider
                min={0}
                max={30}
                step={1}
                value={values.sleepMinutes}
                onChange={noop}
                showValue
                unit={t("mouse.parameter.freqSleepMinuteUnit")}
              />
            </div>
            <Checkbox
              className="checkbox-container"
              label={t("mouse.parameter.neverSleep")}
              checked={values.sleepMinutes === 0}
              onChange={noop}
            />
          </div>
        </FormSection>
        <FormSection
          title={t("mouse.parameter.zhanliKeyRespond")}
          description={t("mouse.parameter.zhanliKeyRespondDesc")}
        >
          <div className="parameter-slider-wrap">
            <Slider
              min={0}
              max={255}
              value={values.debounceTime}
              onChange={noop}
              showValue
              unit="ms"
            />
          </div>
        </FormSection>
      </div>
      <div className="setting-column">
        {/* 对齐 parameter/zhanli：不展示移动唤醒（showWakeup: false） */}
        <FormSection
          title={t("mouse.parameter.rippleCorrection")}
          description={t("mouse.parameter.rippleCorrectionDesc")}
          rightContent={<Switch checked={values.ripple} onChange={noop} />}
        />
        <FormSection
          className="virtual-drift-section"
          title={t("mouse.parameter.virtualDrift")}
          description={t("mouse.parameter.virtualDriftDesc")}
          rightContent={
            <div className="virtual-drift-right">
              <div className="virtual-drift-visual" aria-hidden>
                <div className="virtual-drift-mouse" />
                <span
                  className="virtual-drift-marker"
                  style={{
                    transform: `translate(-50%, calc(-50% + ${(-virtualOffsetDisplayUi / 100) * 28}px))`,
                  }}
                />
              </div>
              <Switch checked={values.virtualOffsetEnable} onChange={noop} />
            </div>
          }
        >
          <div className="virtual-drift-body">
            <div className="parameter-slider-wrap">
              <Slider
                min={-100}
                max={100}
                step={1}
                value={virtualOffsetDisplayUi}
                disabled={!values.virtualOffsetEnable}
                onChange={noop}
                showValue
              />
            </div>
          </div>
        </FormSection>
        <FormSection
          title={t("mouse.parameter.sensorRotation")}
          description={t("mouse.parameter.sensorRotationDesc")}
        >
          <div className="community-preview-readonly-interaction">
            <SensorRotation value={values.angleTune} onChange={noop} />
          </div>
        </FormSection>
      </div>
    </div>
  );
}

function JiangmengParameterPreview({
  meta,
  configData,
}: {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const isAlpha6 = mouseIsAlpha6(meta.mouseDeviceId);
  const values = useMemo(
    () => getMouseParameterPreviewValues(configData),
    [configData]
  );
  const sleepSliderSec = jiangmengSleepLevelToSeconds(
    values.sleepTime,
    isAlpha6
  );

  const sensorType = useMemo(() => {
    const mice = getSupportedDevicesSync().mouse as Array<{
      deviceId?: string;
      sensor?: string;
    }>;
    const match = mice.find((item) => item.deviceId === meta.mouseDeviceId);
    return match?.sensor ?? "3950";
  }, [meta.mouseDeviceId]);

  const lodSlider3955 = isJiangmeng3955LodSlider(sensorType, "jiangmeng");

  const lodOptions = useMemo(
    () => getLodOptionsBySensor(sensorType, "jiangmeng"),
    [sensorType]
  );

  const lodValue = useMemo(() => {
    const inOptions = lodOptions.some((item) => item.value === values.lod);
    return inOptions ? values.lod : (lodOptions[0]?.value ?? values.lod);
  }, [lodOptions, values.lod]);

  const lodMmPreview = useMemo(
    () => jiangmeng3955LodDeviceValueToMm(lodValue),
    [lodValue]
  );

  const noop = () => undefined;

  return (
    <div className="mouse-setting-content">
      <div className="setting-column">
        <FormSection
          title={t("mouse.parameter.motionSync")}
          description={t("mouse.parameter.motionSyncDesc")}
          rightContent={<Switch checked={values.motionSync} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.rippleCorrection")}
          description={t("mouse.parameter.rippleCorrectionDesc")}
          rightContent={<Switch checked={values.ripple} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.lodHeight")}
          description={t("mouse.parameter.lodHeightDesc")}
        >
          {lodSlider3955 ? (
            <div className="parameter-slider-wrap">
              <Slider
                min={JIANGMENG_3955_LOD_MM_MIN}
                max={JIANGMENG_3955_LOD_MM_MAX}
                step={JIANGMENG_3955_LOD_MM_STEP}
                value={lodMmPreview}
                onChange={noop}
                showValue
                unit="mm"
                disabled
                scaleByStep
              />
            </div>
          ) : (
            <RadioGroup
              options={lodOptions}
              value={lodValue}
              onChange={noop}
              direction="horizontal"
            />
          )}
        </FormSection>
        <FormSection
          title={t("mouse.parameter.sleepTime")}
          description={t("mouse.parameter.sleepTimeDesc")}
        >
          <div className="parameter-slider-wrap">
            <Slider
              min={
                isAlpha6 ? ALPHA6_SLEEP_MIN_SEC : JIANGMENG_SLEEP_LEGACY_MIN_SEC
              }
              max={
                isAlpha6 ? ALPHA6_SLEEP_MAX_SEC : JIANGMENG_SLEEP_LEGACY_MAX_SEC
              }
              step={isAlpha6 ? ALPHA6_SLEEP_STEP_SEC : 10}
              value={sleepSliderSec}
              onChange={noop}
              showValue
              unit="s"
            />
          </div>
        </FormSection>
        <FormSection
          title={t("mouse.parameter.debounceDelay")}
          description={t("mouse.parameter.debounceDelayDesc")}
        >
          <div className="parameter-slider-wrap">
            <Slider
              min={0}
              max={15}
              value={values.debounceTime}
              onChange={noop}
              showValue
              unit="ms"
            />
          </div>
        </FormSection>
      </div>
      <div className="setting-column">
        <FormSection
          title={t("mouse.parameter.longDistanceMode")}
          description={t("mouse.parameter.longDistanceModeDesc")}
          rightContent={
            <Switch checked={values.longDistance} onChange={noop} />
          }
        />
        <FormSection
          title={t("mouse.parameter.straightCorrection")}
          description={t("mouse.parameter.straightCorrectionDesc")}
          rightContent={<Switch checked={values.angle} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.fps20kScanTitle")}
          description={t("mouse.parameter.fps20kScanDesc")}
          rightContent={<Switch checked={values.fps20k} onChange={noop} />}
        />
        <FormSection
          title={t("mouse.parameter.sensorRotation")}
          description={t("mouse.parameter.sensorRotationDesc")}
        >
          <div className="community-preview-readonly-interaction">
            <SensorRotation value={values.angleTune} onChange={noop} />
          </div>
        </FormSection>
      </div>
    </div>
  );
}

export default function MouseParameterSection({ meta, configData }: Props) {
  const provider =
    meta.solutionProvider === "freq"
      ? "freq"
      : meta.solutionProvider === "beiying"
        ? "beiying"
        : meta.solutionProvider === "zhanli"
          ? "zhanli"
          : "jiangmeng";

  return (
    <div className="community-mouse-parameter-preview">
      {provider === "freq" ? (
        <FreqParameterPreview meta={meta} configData={configData} />
      ) : provider === "beiying" ? (
        <BeiyingParameterPreview meta={meta} configData={configData} />
      ) : provider === "zhanli" ? (
        <ZhanliParameterPreview meta={meta} configData={configData} />
      ) : (
        <JiangmengParameterPreview meta={meta} configData={configData} />
      )}
    </div>
  );
}
